const STORAGE_PREFIX = 'twine_project_cover_crop:';

function canUseStorage() {
  return typeof window !== 'undefined' && window.localStorage;
}

/**
 * Read a temporarily stored cover crop for a project (client-only).
 * Stored as normalised 0–1 fractions `{ x, y, w, h }`.
 *
 * @param {string|number} projectId
 * @returns {{ x: number, y: number, w: number, h: number, imageUrl?: string } | null}
 */
export function getStoredProjectCoverCrop(projectId) {
  if (!projectId || !canUseStorage()) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + String(projectId));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed) {
      return null;
    }

    // Prefer normalised shape; migrate legacy pixel `{ width, height }` if present
    // by leaving it to the caller (callers should pass natural size).
    if (
      typeof parsed.x === 'number' &&
      typeof parsed.y === 'number' &&
      typeof parsed.w === 'number' &&
      typeof parsed.h === 'number'
    ) {
      return {
        x: parsed.x,
        y: parsed.y,
        w: parsed.w,
        h: parsed.h,
        imageUrl:
          typeof parsed.imageUrl === 'string' ? parsed.imageUrl : undefined,
        naturalWidth:
          typeof parsed.naturalWidth === 'number'
            ? parsed.naturalWidth
            : undefined,
        naturalHeight:
          typeof parsed.naturalHeight === 'number'
            ? parsed.naturalHeight
            : undefined,
      };
    }

    // Legacy pixel storage from earlier modal work.
    if (
      typeof parsed.x === 'number' &&
      typeof parsed.y === 'number' &&
      typeof parsed.width === 'number' &&
      typeof parsed.height === 'number' &&
      typeof parsed.naturalWidth === 'number' &&
      typeof parsed.naturalHeight === 'number' &&
      parsed.naturalWidth > 0 &&
      parsed.naturalHeight > 0
    ) {
      return {
        x: parsed.x / parsed.naturalWidth,
        y: parsed.y / parsed.naturalHeight,
        w: parsed.width / parsed.naturalWidth,
        h: parsed.height / parsed.naturalHeight,
        imageUrl:
          typeof parsed.imageUrl === 'string' ? parsed.imageUrl : undefined,
        naturalWidth: parsed.naturalWidth,
        naturalHeight: parsed.naturalHeight,
      };
    }

    return null;
  } catch (err) {
    return null;
  }
}

/**
 * Persist normalised cover crop coords for a project (client-only; not sent to the API).
 *
 * @param {string|number} projectId
 * @param {{ x: number, y: number, w: number, h: number, imageUrl?: string, naturalWidth?: number, naturalHeight?: number }} crop
 */
export function setStoredProjectCoverCrop(projectId, crop) {
  if (!projectId || !crop || !canUseStorage()) {
    return;
  }

  try {
    window.localStorage.setItem(
      STORAGE_PREFIX + String(projectId),
      JSON.stringify({
        x: crop.x,
        y: crop.y,
        w: crop.w,
        h: crop.h,
        imageUrl: crop.imageUrl || undefined,
        naturalWidth: crop.naturalWidth || undefined,
        naturalHeight: crop.naturalHeight || undefined,
        at: Date.now(),
      })
    );
  } catch (err) {
    // Quota / private mode — ignore
  }
}

/**
 * @param {string|number} projectId
 */
export function clearStoredProjectCoverCrop(projectId) {
  if (!projectId || !canUseStorage()) {
    return;
  }

  try {
    window.localStorage.removeItem(STORAGE_PREFIX + String(projectId));
  } catch (err) {
    // ignore
  }
}
